import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, Bars3Icon, MusicalNoteIcon, ChatBubbleLeftRightIcon, ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { downloadSetlistDocx } from '../utils/setlistDocx';
import { getSongs } from '../services/api';
import SongSelector from './SongSelector';

const EditSetListModal = ({ setlist, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    sets: []
  });
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [uniqueSongs, setUniqueSongs] = useState(true);

  useEffect(() => {
    if (setlist) {
      setFormData({
        name: setlist.name || '',
        date: setlist.date ? new Date(setlist.date).toISOString().split('T')[0] : '',
        sets: setlist.sets || []
      });
    }
    loadSongs();
  }, [setlist]);

  const loadSongs = async () => {
    try {
      const songsData = await getSongs();
      console.log('Loaded songs:', songsData);
      setSongs(songsData);
    } catch (error) {
      console.error('Error loading songs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addNewSet = () => {
    const newSet = {
      name: `Set ${formData.sets.length + 1}`,
      items: []
    };
    setFormData(prev => ({
      ...prev,
      sets: [...prev.sets, newSet]
    }));
  };

  const removeSet = (setIndex) => {
    setFormData(prev => ({
      ...prev,
      sets: prev.sets.filter((_, index) => index !== setIndex)
    }));
  };

  const updateSetName = (setIndex, newName) => {
    setFormData(prev => ({
      ...prev,
      sets: prev.sets.map((set, index) => 
        index === setIndex ? { ...set, name: newName } : set
      )
    }));
  };

  const addSongToSet = (setIndex, songId) => {
    const newItem = {
      type: 'song',
      song_id: songId,
      banter: null
    };
    setFormData(prev => ({
      ...prev,
      sets: prev.sets.map((set, index) => 
        index === setIndex 
          ? { ...set, items: [...set.items, newItem] }
          : set
      )
    }));
  };

  const parseDuration = (input) => {
    if (!input) return 0;
    
    // Check if input is in MM:SS format
    if (input.includes(':')) {
      const parts = input.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseInt(parts[1]) || 0;
        return minutes * 60 + seconds;
      }
    }
    
    // Otherwise treat as seconds
    return parseInt(input) || 0;
  };

  const addBanterToSet = (setIndex) => {
    const banterName = prompt('Enter banter name:');
    const banterDuration = prompt('Enter duration (seconds or MM:SS format):');
    
    if (banterName && banterDuration) {
      const durationInSeconds = parseDuration(banterDuration);
      const newItem = {
        type: 'banter',
        song_id: null,
        banter: {
          name: banterName,
          duration: durationInSeconds
        }
      };
      setFormData(prev => ({
        ...prev,
        sets: prev.sets.map((set, index) => 
          index === setIndex 
            ? { ...set, items: [...set.items, newItem] }
            : set
        )
      }));
    }
  };

  const removeItemFromSet = (setIndex, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      sets: prev.sets.map((set, index) => 
        index === setIndex 
          ? { ...set, items: set.items.filter((_, idx) => idx !== itemIndex) }
          : set
      )
    }));
  };

  const moveItemInSet = (setIndex, fromIndex, toIndex) => {
    setFormData(prev => ({
      ...prev,
      sets: prev.sets.map((set, index) => {
        if (index === setIndex) {
          const newItems = [...set.items];
          const [moved] = newItems.splice(fromIndex, 1);
          newItems.splice(toIndex, 0, moved);
          return { ...set, items: newItems };
        }
        return set;
      })
    }));
  };

  const handleDragStart = (e, setIndex, itemIndex) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ setIndex, itemIndex }));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem({ setIndex, itemIndex });
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetSetIndex, targetItemIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { setIndex: sourceSetIndex, itemIndex: sourceItemIndex } = dragData;
    
    if (sourceSetIndex === targetSetIndex && sourceItemIndex !== targetItemIndex) {
      moveItemInSet(targetSetIndex, sourceItemIndex, targetItemIndex);
    }
    setDraggedItem(null);
  };

  const moveItemUp = (setIndex, itemIndex) => {
    if (itemIndex > 0) {
      moveItemInSet(setIndex, itemIndex, itemIndex - 1);
    }
  };

  const moveItemDown = (setIndex, itemIndex) => {
    const set = formData.sets[setIndex];
    if (itemIndex < set.items.length - 1) {
      moveItemInSet(setIndex, itemIndex, itemIndex + 1);
    }
  };

  const getSongById = (songId) => {
    return songs.find(song => song.id === songId);
  };

  const getSongIdsInSet = (setIndex) => {
    return formData.sets[setIndex]?.items
      .filter(item => item.type === 'song' && item.song_id)
      .map(item => item.song_id) || [];
  };

  const getAllSongIdsInSetlist = () => {
    return formData.sets.flatMap(set =>
      set.items
        .filter(item => item.type === 'song' && item.song_id)
        .map(item => item.song_id)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const setlistData = {
      ...formData,
      date: new Date(formData.date).toISOString()
    };
    onSave(setlistData);
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getSetDuration = (set) => {
    let totalSeconds = 0;
    set.items.forEach(item => {
      if (item.type === 'song' && item.song_id) {
        const song = getSongById(item.song_id);
        if (song && song.duration) {
          totalSeconds += song.duration;
        }
      } else if (item.type === 'banter' && item.banter?.duration) {
        totalSeconds += item.banter.duration;
      }
    });
    return totalSeconds;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Edit Set List</h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => downloadSetlistDocx(
                { name: formData.name, date: formData.date, sets: formData.sets },
                songs
              )}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 flex items-center space-x-1 text-sm"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          {/* Basic Info */}
          <div className="p-6 border-b">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Set List Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleBasicInfoChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Performance Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleBasicInfoChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <label className="flex items-center space-x-2 mt-3">
              <input
                type="checkbox"
                checked={uniqueSongs}
                onChange={(e) => setUniqueSongs(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Unique songs in set list</span>
            </label>
          </div>

          {/* Sets */}
          <div className="flex-1 overflow-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-medium text-gray-900">Sets</h4>
              <button
                type="button"
                onClick={addNewSet}
                className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 flex items-center space-x-1 text-sm"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Set</span>
              </button>
            </div>

            {formData.sets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No sets yet. Add your first set to get started!
              </div>
            ) : (
              <div className="space-y-6">
                {formData.sets.map((set, setIndex) => (
                  <div key={setIndex} className="border border-gray-200 rounded-lg p-4">
                    {/* Set Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={set.name}
                          onChange={(e) => updateSetName(setIndex, e.target.value)}
                          className="text-lg font-medium bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                        />
                        <span className="text-sm text-gray-600 font-medium">
                          ({formatDuration(getSetDuration(set))})
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <SongSelector
                          songs={songs}
                          excludeSongIds={uniqueSongs ? getAllSongIdsInSetlist() : []}
                          onSongSelect={(songId) => addSongToSet(setIndex, songId)}
                        />
                        <button
                          type="button"
                          onClick={() => addBanterToSet(setIndex)}
                          className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 flex items-center space-x-1"
                        >
                          <ChatBubbleLeftRightIcon className="w-4 h-4" />
                          <span>Add Banter</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSet(setIndex)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Set Items */}
                    {set.items.length === 0 ? (
                      <div className="text-center py-4 text-gray-400 text-sm">
                        This set is empty. Add songs or banter to get started.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {set.items.map((item, itemIndex) => {
                          const isDragging = draggedItem && draggedItem.setIndex === setIndex && draggedItem.itemIndex === itemIndex;
                          return (
                            <div
                              key={itemIndex}
                              draggable
                              onDragStart={(e) => handleDragStart(e, setIndex, itemIndex)}
                              onDragEnd={handleDragEnd}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, setIndex, itemIndex)}
                              className={`flex items-center justify-between p-3 rounded border transition-colors ${
                                isDragging 
                                  ? 'bg-blue-100 border-blue-300 opacity-50' 
                                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <Bars3Icon className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
                              {item.type === 'song' ? (
                                <div className="flex items-center space-x-2">
                                  <MusicalNoteIcon className="w-4 h-4 text-blue-600" />
                                  <span>
                                    {getSongById(item.song_id)?.title || 'Unknown Song'} - {getSongById(item.song_id)?.artist || 'Unknown Artist'}
                                  </span>
                                  {getSongById(item.song_id)?.duration && (
                                    <span className="text-sm text-gray-500">
                                      ({formatDuration(getSongById(item.song_id).duration)})
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <ChatBubbleLeftRightIcon className="w-4 h-4 text-purple-600" />
                                  <span>{item.banter?.name || 'Banter'}</span>
                                  <span className="text-sm text-gray-500">
                                    ({formatDuration(item.banter?.duration || 0)})
                                  </span>
                                </div>
                              )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {/* Move Up/Down Buttons */}
                                <div className="flex flex-col">
                                  <button
                                    type="button"
                                    onClick={() => moveItemUp(setIndex, itemIndex)}
                                    disabled={itemIndex === 0}
                                    className={`p-1 text-xs ${
                                      itemIndex === 0 
                                        ? 'text-gray-300 cursor-not-allowed' 
                                        : 'text-gray-600 hover:text-blue-600'
                                    }`}
                                    title="Move up"
                                  >
                                    <ChevronUpIcon className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveItemDown(setIndex, itemIndex)}
                                    disabled={itemIndex === set.items.length - 1}
                                    className={`p-1 text-xs ${
                                      itemIndex === set.items.length - 1 
                                        ? 'text-gray-300 cursor-not-allowed' 
                                        : 'text-gray-600 hover:text-blue-600'
                                    }`}
                                    title="Move down"
                                  >
                                    <ChevronDownIcon className="w-3 h-3" />
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeItemFromSet(setIndex, itemIndex)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Set List
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSetListModal;